import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Lightning, Star, Crown, ArrowRight, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useMonetizationMode } from '../hooks/useEntitlement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = { player_free: Star, player_plus: Lightning, player_pro: Crown };
const PLAN_ACCENTS = { player_free: '#888', player_plus: '#00F0FF', player_pro: '#F59E0B' };

const FEATURE_LABELS = {
  'ai.rule_questions': 'AI Rule Questions',
  'analytics.advanced': 'Advanced Analytics',
  'analytics.comparisons': 'Stat Comparisons & Trends',
  'analytics.exports': 'Data Exports',
  'matchmaking.enhanced': 'Enhanced Matchmaking',
  'discovery.saved_filters': 'Saved Filters & Alerts',
  'profile.premium_flair': 'Premium Profile Flair',
  'profile.customization': 'Profile Customization',
};

function formatEntitlement(ent) {
  if (!ent) return { text: '—', enabled: false };
  if (ent.type === 'boolean') return { text: ent.value === 'true' ? 'Included' : '—', enabled: ent.value === 'true' };
  if (ent.type === 'limit') {
    const v = parseInt(ent.value);
    if (v >= 9999) return { text: 'Unlimited', enabled: true };
    return { text: `${v}/month`, enabled: true };
  }
  return { text: ent.value, enabled: true };
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const monetization = useMonetizationMode();

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/monetization/plans/compare?tenant_type=player`),
      axios.get(`${API_URL}/api/monetization/my/subscription`, { withCredentials: true }).catch(() => ({ data: { plan_code: 'player_free' } })),
    ]).then(([cmp, sub]) => {
      setComparison(cmp.data);
      setMySub(sub.data);
    }).finally(() => setLoading(false));
  }, []);

  const subscribe = async (planCode) => {
    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/api/monetization/my/subscribe`, { plan_code: planCode }, { withCredentials: true });
      toast.success(planCode === 'player_free' ? 'Downgraded to Free' : 'Subscription updated!');
      const { data } = await axios.get(`${API_URL}/api/monetization/my/subscription`, { withCredentials: true });
      setMySub(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubscribing(false); }
  };

  if (loading) return (
    <div className="app-container py-8">
      <div className="space-y-4">
        <div className="h-10 w-64 bg-white/5 rounded animate-pulse mx-auto" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-96 bg-white/[0.03] rounded-xl animate-pulse" />)}
        </div>
      </div>
    </div>
  );

  const plans = comparison?.plans || [];
  const features = comparison?.features || [];

  return (
    <div className="app-container py-6 pb-24" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Level Up Your Game</h1>
        <p className="text-[14px] text-[#555] mt-3 max-w-md mx-auto">Choose the plan that fits your play style. Upgrade anytime.</p>
      </div>

      {/* Free during early access banner */}
      {monetization.mode === 'disabled' && (
        <div className="max-w-2xl mx-auto mb-8 px-5 py-4 rounded-xl border border-[#10B981]/30 bg-gradient-to-r from-[#10B981]/10 to-[#00F0FF]/5"
          data-testid="free-early-access-banner">
          <div className="flex items-center gap-3">
            <Sparkle size={22} weight="fill" className="text-[#10B981] flex-shrink-0" />
            <div>
              <p className="text-white font-semibold text-sm">Free during early access</p>
              <p className="text-[#888] text-[12px] mt-0.5">All features are unlocked for everyone while we grow the community. Plans below are previews of future paid tiers.</p>
            </div>
          </div>
        </div>
      )}
      {monetization.mode === 'soft' && (
        <div className="max-w-2xl mx-auto mb-8 px-5 py-4 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5"
          data-testid="soft-mode-banner">
          <p className="text-[#F59E0B] font-semibold text-sm">Plans are visible but not enforced yet — pick the tier that fits, and we'll let you know before paid plans go live.</p>
        </div>
      )}

      {/* AI Usage */}
      {mySub?.ai_usage && (
        <div className="max-w-sm mx-auto mb-8 px-4 py-3 rounded-lg bg-[#0c0c14] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-[#555] uppercase tracking-wider">AI Questions This Month</span>
            <span className="text-[12px] text-white font-medium tabular-nums">
              {mySub.ai_usage.used} / {mySub.ai_usage.limit >= 9999 ? '∞' : mySub.ai_usage.limit}
            </span>
          </div>
          <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${mySub.ai_usage.limit >= 9999 ? 10 : Math.min(100, (mySub.ai_usage.used / mySub.ai_usage.limit) * 100)}%`,
                backgroundColor: mySub.ai_usage.remaining <= 5 ? '#EF4444' : '#00F0FF',
              }} />
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
        {plans.map((plan, idx) => {
          const Icon = PLAN_ICONS[plan.plan_code] || Star;
          const accent = PLAN_ACCENTS[plan.plan_code] || '#888';
          const isCurrent = mySub?.plan_code === plan.plan_code;
          const isPopular = plan.plan_code === 'player_plus';

          return (
            <div key={plan.plan_code}
              className={`relative rounded-xl border p-5 flex flex-col transition-all ${
                isCurrent
                  ? `border-[${accent}]/40 bg-[${accent}]/[0.03]`
                  : 'border-white/[0.06] bg-[#0c0c14] hover:border-white/[0.12]'
              }`}
              style={isCurrent ? { borderColor: `${accent}40`, backgroundColor: `${accent}05` } : {}}
              data-testid={`plan-card-${plan.plan_code}`}>

              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: accent, color: '#000' }}>Most Popular</div>
              )}

              {isCurrent && (
                <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#10B981] text-white">Current</div>
              )}

              <div className="flex items-center gap-2 mb-3 mt-1">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
                  <Icon size={18} weight="duotone" style={{ color: accent }} />
                </div>
                <div>
                  <p className="text-white font-bold text-[15px]">{plan.name}</p>
                  <p className="text-[11px] text-[#444]">{plan.description}</p>
                </div>
              </div>

              <div className="mb-4">
                {plan.monthly_price === 0 ? (
                  <p className="text-2xl font-bold text-white">Free</p>
                ) : (
                  <p className="text-2xl font-bold text-white">
                    ${plan.monthly_price}<span className="text-[13px] text-[#555] font-normal">/mo</span>
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="flex-1 space-y-2 mb-5">
                {features.map(f => {
                  const ent = plan.entitlements[f.feature_code];
                  const fmt = formatEntitlement(ent);
                  return (
                    <div key={f.feature_code} className="flex items-center gap-2">
                      {fmt.enabled ? (
                        <Check size={14} weight="bold" style={{ color: accent }} />
                      ) : (
                        <X size={14} className="text-[#2a2a2a]" />
                      )}
                      <span className={`text-[12px] ${fmt.enabled ? 'text-[#999]' : 'text-[#333]'}`}>
                        {FEATURE_LABELS[f.feature_code] || f.feature_name}
                        {fmt.enabled && fmt.text !== 'Included' && (
                          <span className="text-[10px] ml-1" style={{ color: `${accent}aa` }}>({fmt.text})</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => subscribe(plan.plan_code)}
                disabled={isCurrent || subscribing}
                className={`w-full py-2.5 rounded-lg text-[13px] font-semibold transition-all flex items-center justify-center gap-2 ${
                  isCurrent
                    ? 'bg-white/5 text-[#444] cursor-default'
                    : `text-white hover:opacity-90`
                }`}
                style={!isCurrent ? { backgroundColor: accent } : {}}
                data-testid={`subscribe-${plan.plan_code}`}>
                {isCurrent ? 'Current Plan' : subscribing ? 'Processing...' : plan.monthly_price === 0 ? 'Downgrade' : 'Upgrade'}
                {!isCurrent && <ArrowRight size={14} />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Shop/Publisher CTA */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 flex items-center justify-between">
          <div>
            <p className="text-white font-semibold text-[14px]">Own a Game Shop?</p>
            <p className="text-[12px] text-[#555]">Plans starting at $20/mo for events, tournaments, and discovery.</p>
          </div>
          <button onClick={() => navigate('/register-shop')}
            className="px-4 py-2 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-[12px] font-semibold whitespace-nowrap"
            data-testid="shop-plans-cta">Sign up as Shop</button>
        </div>
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 flex items-center justify-between">
          <div>
            <p className="text-white font-semibold text-[14px]">Game Publisher?</p>
            <p className="text-[12px] text-[#555]">Invite-only publisher accounts for content management.</p>
          </div>
          <button onClick={() => navigate('/publisher-onboarding')}
            className="px-4 py-2 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] text-[12px] font-semibold whitespace-nowrap"
            data-testid="publisher-plans-cta">Learn More</button>
        </div>
      </div>
    </div>
  );
}
